import { useMemo } from 'react';
import midnightOfDate, { secondsPerDay } from 'utils/midnightOfDate';
import { formatTimestampInTimezone } from 'utils/time';

export const useDayItems = (utcOffset: number) => {
  const dates = useMemo(() => {
    const midnightTimestampInSpotTimezone = midnightOfDate(utcOffset);
    const namedDays = ['Today', 'Yesterday'];
    let timestamp = midnightTimestampInSpotTimezone;
    // eslint-disable-next-line @typescript-eslint/no-array-constructor
    return new Array(0, 0, 0, 0, 0, 0).map((_, ind) => {
      const option = {
        value: formatTimestampInTimezone(timestamp, utcOffset, 'yyyy-MM-dd'),
        label: namedDays[ind] || formatTimestampInTimezone(timestamp, utcOffset, 'EEE MMM dd yyyy'),
      };
      timestamp -= secondsPerDay;
      return option;
    });
  }, [utcOffset]);
  return dates;
};

export const getWaveSizeItems = (surfHeightSetting: string) => {
  const waveSizeItems = [];
  if (surfHeightSetting === 'FT') {
    const items = [
      {
        value: '0',
        label: 'Flat',
      },
      {
        value: '0-1',
        label: '0-1 ft',
      },
      {
        value: '1-2',
        label: '1-2 ft',
      },
      {
        value: '2-3',
        label: '2-3 ft',
      },
      {
        value: '2-3+',
        label: '2-3 ft +',
      },
      {
        value: '3-4',
        label: '3-4 ft',
      },
      {
        value: '3-4+',
        label: '3-4 ft +',
      },
      {
        value: '3-5',
        label: '3-5 ft',
      },
      {
        value: '4-5',
        label: '4-5 ft',
      },
      {
        value: '4-6',
        label: '4-6 ft',
      },
      {
        value: '4-6+',
        label: '4-6 ft +',
      },
      {
        value: '5-7',
        label: '5-7 ft',
      },
      {
        value: '5-8',
        label: '5-8 ft',
      },
      {
        value: '6-8',
        label: '6-8 ft',
      },
      {
        value: '6-10',
        label: '6-10 ft',
      },
      {
        value: '8-10',
        label: '8-10 ft',
      },
      {
        value: '8-12',
        label: '8-12 ft',
      },
      {
        value: '10-15',
        label: '10-15 ft',
      },
      {
        value: '15-18',
        label: '15-18 ft',
      },
      {
        value: '15-20',
        label: '15-20 ft',
      },
      {
        value: '20-25',
        label: '20-25 ft',
      },
      {
        value: '25-30',
        label: '25-30 ft',
      },
      {
        value: '30-35',
        label: '30-35 ft',
      },
      {
        value: '30-40',
        label: '30-40 ft',
      },
      {
        value: '35-50',
        label: '35-50 ft',
      },
    ];
    waveSizeItems.push(...items);
  } else if (surfHeightSetting === 'M') {
    const items = [
      {
        value: '0',
        label: '0',
      },
      {
        value: '0.2-0.3',
        label: '0.2-0.3 m',
      },
      {
        value: '0.3-0.6',
        label: '0.3-0.6 m',
      },
      {
        value: '0.6-0.9',
        label: '0.6-0.9 m',
      },
      {
        value: '0.6-1.1',
        label: '0.6-1.1 m',
      },
      {
        value: '0.9-1.2',
        label: '0.9-1.2 m',
      },
      {
        value: '0.9-1.4',
        label: '0.9-1.4 m',
      },
      {
        value: '0.9-1.5',
        label: '0.9-1.5 m',
      },
      {
        value: '1.2-1.5',
        label: '1.2-1.5 m',
      },
      {
        value: '1.2-1.8',
        label: '1.2-1.8 m',
      },
      {
        value: '1.2-2',
        label: '1.2-2 m',
      },
      {
        value: '1.5-2.1',
        label: '1.5-2.1 m',
      },
      {
        value: '1.8-2.4',
        label: '1.8-2.4 m',
      },
      {
        value: '1.8-3.0',
        label: '1.8-3.0 m',
      },
      {
        value: '2.4-3.0',
        label: '2.4-3.0 m',
      },
      {
        value: '2.4-3.7',
        label: '2.4-3.7 m',
      },
      {
        value: '3.0-4.5',
        label: '3.0-4.5 m',
      },
      {
        value: '4.5-5.5',
        label: '4.5-5.5 m',
      },
      {
        value: '4.5-6.1',
        label: '4.5-6.1 m',
      },
      {
        value: '6.1-7.6',
        label: '6.1-7.6 m',
      },
      {
        value: '7.6-9.1',
        label: '7.6-9.1 m',
      },
      {
        value: '9.1-10.7',
        label: '9.1-10.7 m',
      },
      {
        value: '9.1-12.2',
        label: '9.1-12.2 m',
      },
      {
        value: '10.7-15.2',
        label: '10.7-15.2 m',
      },
    ];
    waveSizeItems.push(...items);
  } else {
    // Defaults to HI values
    const items = [
      {
        value: '0',
        label: 'Flat',
      },
      {
        value: '0-0.5',
        label: '0-0.5 ft',
      },
      {
        value: '0.5-1',
        label: '0.5-1 ft',
      },
      {
        value: '1-2',
        label: '1-2 ft',
      },
      {
        value: '1-2+',
        label: '1-2 ft +',
      },
      {
        value: '1-3',
        label: '1-3 ft',
      },
      {
        value: '2-3',
        label: '2-3 ft',
      },
      {
        value: '2-3+',
        label: '2-3 ft +',
      },
      {
        value: '2-4',
        label: '2-4 ft',
      },
      {
        value: '3-4',
        label: '3-4 ft',
      },
      {
        value: '3-4+',
        label: '3-4 ft +',
      },
      {
        value: '3-5',
        label: '3-5 ft',
      },
      {
        value: '4-5',
        label: '4-5 ft',
      },
      {
        value: '4-6',
        label: '4-6 ft',
      },
      {
        value: '6-8',
        label: '6-8 ft',
      },
      {
        value: '8-10',
        label: '8-10 ft',
      },
      {
        value: '10-12',
        label: '10-12 ft',
      },
      {
        value: '12-15',
        label: '12-15 ft',
      },
      {
        value: '15-18',
        label: '15-18 ft',
      },
      {
        value: '15-20',
        label: '15-20 ft',
      },
      {
        value: '18-20',
        label: '18-20 ft',
      },
      {
        value: '20-25',
        label: '20-25 ft',
      },
    ];
    waveSizeItems.push(...items);
  }
  return waveSizeItems;
};

export const getConditionItems = () => {
  const conditionItems = [
    {
      value: 'VERY_POOR',
      label: 'Very Poor',
    },
    {
      value: 'POOR',
      label: 'Poor',
    },
    {
      value: 'POOR_TO_FAIR',
      label: 'Poor to Fair',
    },
    {
      value: 'FAIR',
      label: 'Fair',
    },
    {
      value: 'FAIR_TO_GOOD',
      label: 'Fair to Good',
    },
    {
      value: 'GOOD',
      label: 'Good',
    },
    {
      value: 'EPIC',
      label: 'Epic',
    },
  ];
  return conditionItems;
};
